import {
  DELETE_CARD_SUCCESS,
  DISABLE_CARD_ABSTRACTION,
  EDIT_CARD_SUCCESS
} from 'redux/constants';
import { REQ_STATUSES } from 'redux/constants/global';
import {
  SET_CREATED_SUBSCRIPTION_DATA,
  SET_SUBSCRIPTION_PAYMENT_DATA,
  GET_SUBSCRIPTION_PAGE_FAIL,
  GET_SUBSCRIPTION_PAGE_START,
  GET_SUBSCRIPTION_PAGE_SUCCESS,
  CLEAR_SUBSCRIPTION_PAYMENT_DATA,
  CREATE_SUBSCRIPTION_FAILED,
  CREATE_SUBSCRIPTION_PAYMENT_FAILED,
  CREATE_SUBSCRIPTION_PAYMENT_START,
  CREATE_SUBSCRIPTION_PAYMENT_SUCCESS,
  CREATE_SUBSCRIPTION_START,
  CREATE_SUBSCRIPTION_SUCCESS,
  SET_SUBSCRIPTION_VIEW,
  SET_DISPLAY_ADDRESS_FIELDS,
  SET_SUBSCRIPTION_SELECTED_CATEGORY,
  SET_SUBSCRIPTION_SELECTED_BANK_PAYMENT_METHOD,
  RESET_SUBSCRIPTION_SELECTED_BANK_PAYMENT_METHOD
} from 'redux/constants/subscription';
import { isEmpty } from 'lodash-es';
import { IAction } from 'models';
import { IPayment } from 'models/payment';

const initialSubscriptionPaymentData = {
  status: REQ_STATUSES.IDLE as keyof typeof REQ_STATUSES,
  data: {}
};

const initialCreatedSubscription = {
  status: REQ_STATUSES.IDLE as keyof typeof REQ_STATUSES,
  data: {},
  error: null
};

export enum SubscriptionViews {
  SUBSCRIPTION_CARDS_LIST = 'SUBSCRIPTION_CARDS_LIST',
  SUBSCRIPTION_CARD_PAYMENT_FORM = 'SUBSCRIPTION_CARD_PAYMENT_FORM',
  SUBSCRIPTION_BANK_PAYMENT_FORM = 'SUBSCRIPTION_BANK_PAYMENT_FORM',
  SUBSCRIPTION_BANK_LIST = 'SUBSCRIPTION_BANK_LIST'
}

export interface ISubscriptionStore {
  subscriptionView: SubscriptionViews;
  subscriptionPage: {
    status: keyof typeof REQ_STATUSES;
    data: Record<string, any>;
    error: string | null | undefined;
  };
  subscriptionPayment: {
    status: keyof typeof REQ_STATUSES;
    data: Record<string, any>;
  };
  createdSubscription: {
    status: keyof typeof REQ_STATUSES;
    data: Record<string, any>;
    error: string | null | undefined;
  };
  isDisplayAddresFields: boolean;
  subscriptionSelectedCategory: string;
  subscriptionSelectedBankPaymentMethod: string;
  subscriptionFirstPayment: IPayment | null;
}

const initialState: ISubscriptionStore = {
  subscriptionView: SubscriptionViews.SUBSCRIPTION_CARD_PAYMENT_FORM,
  subscriptionPage: {
    status: REQ_STATUSES.IDLE as keyof typeof REQ_STATUSES,
    data: {},
    error: null
  },
  subscriptionPayment: initialSubscriptionPaymentData,
  createdSubscription: initialCreatedSubscription,
  isDisplayAddresFields: false,
  subscriptionSelectedCategory: '',
  subscriptionSelectedBankPaymentMethod: '',
  subscriptionFirstPayment: null
};

const setSubscriptionPageStatus = (
  state: ISubscriptionStore,
  newStatus: keyof typeof REQ_STATUSES
) => ({
  ...state,
  subscriptionPage: {
    ...state.subscriptionPage,
    status: newStatus,
    error: null
  }
});

const setSubscriptionPaymentStatus = (
  state: ISubscriptionStore,
  newStatus: keyof typeof REQ_STATUSES
) => ({
  ...state,
  subscriptionPayment: {
    ...state.subscriptionPayment,
    status: newStatus
  }
});

const setCreatedSubscriptionStatus = (
  state: ISubscriptionStore,
  newStatus: keyof typeof REQ_STATUSES
) => ({
  ...state,
  createdSubscription: {
    ...state.createdSubscription,
    status: newStatus,
    error: null
  }
});

const subscriptionReducer = (
  state = initialState,
  { type, payload }: IAction<string, any>
) => {
  switch (type) {
    case GET_SUBSCRIPTION_PAGE_START: {
      return setSubscriptionPageStatus(
        state,
        REQ_STATUSES.PENDING as keyof typeof REQ_STATUSES
      );
    }
    case GET_SUBSCRIPTION_PAGE_SUCCESS: {
      const hasCreatedSubscription = !isEmpty(payload?.data?.created_subscription?.id);
      const hasCreatedPayment = !isEmpty(payload?.data?.payment?.id);
      return {
        ...state,
        subscriptionPage: {
          ...state?.subscriptionPage,
          data: {
            ...state?.subscriptionPage?.data,
            ...payload?.data
          },
          status: REQ_STATUSES.RESOLVED,
          error: null
        },
        ...(hasCreatedSubscription
          ? {
              createdSubscription: {
                ...state?.createdSubscription,
                status: REQ_STATUSES.RESOLVED,
                data: {
                  ...state?.createdSubscription?.data,
                  ...payload?.data?.created_subscription
                },
                error: null
              }
            }
          : {}),
        ...(hasCreatedPayment
          ? {
              subscriptionPayment: {
                ...state?.subscriptionPayment,
                status: REQ_STATUSES.RESOLVED,
                data: {
                  ...state?.subscriptionPayment?.data,
                  ...payload?.data?.payment
                },
                error: null
              }
            }
          : {}),
        subscriptionFirstPayment: payload?.data?.subscription_first_payment || {}
      };
    }
    case GET_SUBSCRIPTION_PAGE_FAIL: {
      return {
        ...state,
        subscriptionPage: {
          ...state.subscriptionPage,
          error: payload?.error,
          status: REQ_STATUSES.REJECTED
        }
      };
    }

    case CREATE_SUBSCRIPTION_PAYMENT_START: {
      return setSubscriptionPaymentStatus(
        state,
        REQ_STATUSES.PENDING as keyof typeof REQ_STATUSES
      );
    }
    case CREATE_SUBSCRIPTION_PAYMENT_SUCCESS: {
      return setSubscriptionPaymentStatus(
        state,
        REQ_STATUSES.RESOLVED as keyof typeof REQ_STATUSES
      );
    }
    case CREATE_SUBSCRIPTION_PAYMENT_FAILED: {
      return setSubscriptionPaymentStatus(
        state,
        REQ_STATUSES.IDLE as keyof typeof REQ_STATUSES
      );
    }
    case SET_SUBSCRIPTION_PAYMENT_DATA: {
      const { page_update_obj: pageUpdateObj } = payload || {};
      const { status, payments_metadata: paymentsMetadata } = pageUpdateObj || {};
      return {
        ...state,
        subscriptionPage: {
          ...state.subscriptionPage,
          data: {
            ...state.subscriptionPage.data,
            status,
            payments_metadata: paymentsMetadata,
            payment: payload.payment
          }
        }
      };
    }

    case CREATE_SUBSCRIPTION_START: {
      return setCreatedSubscriptionStatus(
        state,
        REQ_STATUSES.PENDING as keyof typeof REQ_STATUSES
      );
    }
    case CREATE_SUBSCRIPTION_SUCCESS: {
      return setCreatedSubscriptionStatus(
        state,
        REQ_STATUSES.RESOLVED as keyof typeof REQ_STATUSES
      );
    }
    case CREATE_SUBSCRIPTION_FAILED: {
      return setCreatedSubscriptionStatus(
        state,
        REQ_STATUSES.REJECTED as keyof typeof REQ_STATUSES
      );
    }
    case SET_CREATED_SUBSCRIPTION_DATA: {
      const {
        page_update_obj: pageUpdateObj,
        subscription_first_payment: subscriptionFirstPayment
      } = payload || {};
      const { status, payments_metadata: paymentsMetadata } = pageUpdateObj || {};
      return {
        ...state,
        subscriptionPage: {
          ...state.subscriptionPage,
          data: {
            ...state.subscriptionPage.data,
            status,
            payments_metadata: paymentsMetadata
          }
        },
        createdSubscription: {
          ...state.createdSubscription,
          data: payload.subscription
        },
        subscriptionFirstPayment
      };
    }
    case CLEAR_SUBSCRIPTION_PAYMENT_DATA: {
      return {
        ...state,
        subscriptionPayment: initialSubscriptionPaymentData
      };
    }
    case EDIT_CARD_SUCCESS:
      return {
        ...state,
        subscriptionPage: {
          ...state.subscriptionPage,
          loading: false,
          error: ''
        }
      };
    case DELETE_CARD_SUCCESS: {
      return {
        ...state,
        subscriptionPage: {
          ...state.subscriptionPage,
          loading: false,
          error: ''
        }
      };
    }
    case DISABLE_CARD_ABSTRACTION: {
      return setSubscriptionPaymentStatus(
        state,
        REQ_STATUSES.IDLE as keyof typeof REQ_STATUSES
      );
    }
    case SET_SUBSCRIPTION_VIEW: {
      return {
        ...state,
        subscriptionView: payload
      };
    }
    case SET_DISPLAY_ADDRESS_FIELDS: {
      return {
        ...state,
        isDisplayAddresFields: payload
      };
    }
    case SET_SUBSCRIPTION_SELECTED_CATEGORY: {
      return {
        ...state,
        subscriptionSelectedCategory: payload
      };
    }
    case SET_SUBSCRIPTION_SELECTED_BANK_PAYMENT_METHOD: {
      return {
        ...state,
        subscriptionSelectedBankPaymentMethod: payload
      };
    }
    case RESET_SUBSCRIPTION_SELECTED_BANK_PAYMENT_METHOD: {
      return {
        ...state,
        subscriptionSelectedBankPaymentMethod: ''
      };
    }
    default:
      return state;
  }
};

export default subscriptionReducer;
